<template>
  <!-- 처음 받은 비밀번호 재설정(appserver 결정 47·48) — 사업자 정보 → 대표자 휴대폰 확인 → 새 비밀번호 두 번. 흐름·문구는 `utils/passwordReset` 한 곳이다 -->
  <div class="reset-panel">
    <p class="reset-lead">{{ $t(text.lead) }}</p>
    <p class="reset-account">{{ accountId }}</p>

    <p class="reset-step">{{ $t(view.title) }}</p>
    <p class="reset-guide">{{ $t(view.guide) }}</p>

    <form v-if="flow.step === step.BUSINESS" @submit.prevent="submitBusiness">
      <!-- 맞지 않는 칸(서버 `fields`)·빈 칸만 테두리로 강조한다 — 칸의 문장은 아래 한 줄(서버 `error`)이 말한다 -->
      <el-input
        v-for="field in businessFields"
        :key="field.key"
        :value="business[field.key]"
        :placeholder="$t(field.placeholder)"
        :name="field.key"
        :inputmode="field.digits ? 'numeric' : 'text'"
        :class="{ 'reset-field--error': flow.fields.indexOf(field.key) > -1 }"
        autocomplete="off"
        clearable
        @input="onBusinessInput(field.key, $event)"></el-input>
      <el-button type="primary" native-type="submit" class="btn_login">{{ $t(text.businessSubmit) }}</el-button>
    </form>

    <el-button v-else-if="flow.step === step.VERIFY" type="primary" class="btn_login" @click="openVerify">{{
      $t(view.verifyButton)
    }}</el-button>

    <form v-else @submit.prevent="submit">
      <el-input
        v-model="password"
        :placeholder="$t(text.newPassword)"
        type="password"
        name="newPassword"
        autocomplete="new-password"
        clearable></el-input>
      <el-input
        v-model="confirm"
        :placeholder="$t(text.confirmPassword)"
        type="password"
        name="confirmPassword"
        autocomplete="new-password"
        clearable></el-input>
      <el-button type="primary" native-type="submit" class="btn_login">{{ $t(text.submit) }}</el-button>
    </form>

    <div class="error-messages">
      <div v-if="flow.message" id="reset-msg" class="msg">{{ $t(flow.message) }}</div>
    </div>

    <!-- 사업자 정보가 없거나 맞지 않을 때·명의가 다를 때 — 고객센터가 사업자등록증대로 바로잡는다. 채팅에 미리 쓴 말을 넣을 수 없어 아이디를 같이 보인다 -->
    <div v-if="view.support" class="reset-support">
      <el-button class="btn_login" @click="openSupport">{{ $t(text.support) }}</el-button>
      <p class="reset-support-id">{{ $t(supportId) }}</p>
    </div>

    <div class="forget_pw">
      <el-button type="text" @click="$emit('leave', '')">{{ $t(backText) }}</el-button>
    </div>

    <!-- 휴대폰 확인 창 — 사장님 비밀번호 찾기와 같은 창이다(`PhoneVerifyDialog`). 돌아오면 열쇠(또는 실패)를 알린다 -->
    <phone-verify-dialog
      :visible.sync="verifyVisible"
      :title="$t(text.verifyButton)"
      @outcome="onVerified"></phone-verify-dialog>
  </div>
</template>

<script>
import PhoneVerifyDialog from '@/components/login/PhoneVerifyDialog.vue'
import { KAKAO_SUPPORT_CHAT_URL } from '@/utils/constants'
import {
  BUSINESS_FIELDS,
  PASSWORD_RESET_CHECK_URL,
  PASSWORD_RESET_TEXT,
  PASSWORD_RESET_URL,
  RESET_STEP,
  afterCheck,
  afterSubmitFailure,
  afterVerify,
  businessInput,
  businessProblem,
  checkBody,
  emptyBusiness,
  newPasswordProblem,
  passwordResetBody,
  resetStart,
  resetView,
  supportIdText,
} from '@/utils/passwordReset'
import { logHttpFailure } from '@/plugins/http'

/** 확인·재설정 요청 — 토스트를 끈다. 실패는 이 칸 한 곳이 말한다(`afterSubmitFailure`) */
const RESET_REQUEST_CONFIG = Object.freeze({ showErrorMessage: false })

/**
 * 처음 받은 비밀번호 재설정 칸 — 로그인 화면이 401 `PASSWORD_RESET_REQUIRED`(막기)를 받거나, 로그인 응답의 경고에서 「지금 바꾸기」를
 * 고르면 로그인 칸 대신 띄운다.
 *
 * 끝은 둘이다: `done(문장)` — 바꿨다 · `leave(문장)` — 바꾸지 않고 나간다(토큰이 끝났거나 사람이 돌아갔다). 그 뒤 어디로 가는지는 부른
 * 화면이 정한다(로그인 전 막기 → 로그인 칸, 로그인 뒤 경고 → 그대로 들어감). 이 칸은 세션을 열지 않는다.
 */
export default {
  name: 'PasswordResetPanel',
  components: { PhoneVerifyDialog },
  props: {
    /** 로그인 관문이 준 재설정 토큰 */
    resetToken: { type: String, required: true },
    /** 로그인 칸에 넣었던 아이디 — 어느 계정인지 보여 주고, 고객센터에 알릴 아이디로도 보인다 */
    accountId: { type: String, default: '' },
    /** 바꾸지 않고 나가는 단추의 말 — 로그인 전(막기)은 「로그인 화면으로」, 로그인 뒤 경고에서 왔으면 「나중에」 */
    backText: { type: String, default: PASSWORD_RESET_TEXT.back },
  },
  data() {
    return {
      text: PASSWORD_RESET_TEXT,
      step: RESET_STEP,
      businessFields: BUSINESS_FIELDS,
      flow: resetStart(),
      /** 1단계 입력 — 통과한 셋은 `flow.business` 가 붙든다 */
      business: emptyBusiness(),
      verifyVisible: false,
      password: '',
      confirm: '',
      /** 보내는 중 — 두 번째 누름을 보내지 않는다(먼저 끝난 두 번째가 소진된 토큰으로 「확인 시간이 지났습니다」를 말하면 거짓이다) */
      submitting: false,
    }
  },
  computed: {
    view() {
      return resetView(this.flow)
    },
    supportId() {
      return supportIdText(this.accountId)
    },
  },
  methods: {
    onBusinessInput(key, value) {
      this.business = businessInput(this.business, key, value)
    },
    /** 1단계 — 빈 칸이 없으면 셋을 확인 입구로 보낸다 */
    submitBusiness() {
      const problem = businessProblem(this.business)
      if (problem) {
        this.flow = { ...this.flow, ...problem }
        return
      }

      this.check(checkBody(this.resetToken, this.business, null))
    },
    openVerify() {
      this.verifyVisible = true
    },
    /** 휴대폰 확인 창이 돌아왔다(창은 이미 닫혔다) — 열쇠가 왔으면 통과한 셋과 그 열쇠로 확인 입구를 부른다(2단계) */
    onVerified(outcome) {
      const next = afterVerify(this.flow, outcome)
      this.flow = next.state
      if (next.check) this.check(checkBody(this.resetToken, this.flow.business, this.flow.verification))
    },
    /** 확인 입구(1·2단계) — 통과면 `next` 의 단계로 */
    async check(body) {
      if (this.submitting) return

      this.submitting = true
      try {
        const result = await this.$axios.post(PASSWORD_RESET_CHECK_URL, body, RESET_REQUEST_CONFIG)
        this.flow = afterCheck(this.flow, body, result.data)
      } catch (error) {
        this.rejected(error, 'password reset check failed: ')
      } finally {
        this.submitting = false
      }
    },
    async submit() {
      if (this.submitting) return
      const problem = newPasswordProblem(this.password, this.confirm)
      if (problem) {
        this.flow = { ...this.flow, message: problem }
        return
      }

      this.submitting = true
      try {
        const result = await this.$axios.post(
          PASSWORD_RESET_URL,
          passwordResetBody(this.resetToken, this.flow, this.password),
          RESET_REQUEST_CONFIG
        )
        this.$emit('done', (result.data && result.data.message) || PASSWORD_RESET_TEXT.done)
      } catch (error) {
        this.rejected(error, 'password reset failed: ')
      } finally {
        this.submitting = false
      }
    },
    /** 확인·재설정이 거절됐다 — 어느 단계로 갈지·떠날지는 `afterSubmitFailure` 한 곳이 정한다 */
    rejected(error, label) {
      const next = afterSubmitFailure(this.flow, error)
      if (next.leave != null) {
        this.$emit('leave', next.leave)
        return
      }
      this.flow = next.state

      // 본문(토큰·사업자 정보·비밀번호)을 통째로 찍지 않는다 — 요약 한 곳을 지난다
      logHttpFailure(error, label)
    },
    openSupport() {
      window.open(KAKAO_SUPPORT_CHAT_URL, '_blank', 'noopener')
    },
  },
}
</script>

<style scoped>
.reset-panel {
  text-align: center;
  padding-top: 20px;
}
.reset-lead {
  text-align: left;
  font-size: 15px;
  line-height: 22px;
  margin-bottom: 8px;
}
.reset-account {
  text-align: left;
  font-weight: 600;
  margin-bottom: 20px;
}
.reset-step {
  text-align: left;
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 6px;
}
.reset-guide {
  text-align: left;
  font-size: 14px;
  line-height: 20px;
  margin-bottom: 12px;
}
.el-input {
  margin-bottom: 20px;
}
.reset-field--error >>> .el-input__inner {
  border-color: var(--color-danger);
}
.error-messages {
  margin: 15px 0;
  min-height: 20px;
}
.msg {
  width: 100%;
  text-align: left;
  font-size: 14px;
  line-height: 16px;
  color: red;
  display: block;
}
.btn_login {
  width: 100%;
  height: 42px;
  font-size: 15px;
  font-weight: 600;
  border-radius: var(--radius-sm);
}
.reset-support {
  margin-bottom: 12px;
}
.reset-support-id {
  text-align: left;
  font-size: 13px;
  line-height: 18px;
  margin-top: 8px;
  color: var(--color-text-secondary);
}
.forget_pw {
  text-align: right;
  margin-bottom: 20px;
}
</style>
